import React, { useState, useMemo } from "react";
import ReactSlider from "react-slider";
import { useQueries } from "@tanstack/react-query";
import { fetchOnlyData } from "../../utility/adminApi";
import SelectLabels from "../../components/SelectLabelsPayMethod";
import Date from "../../super_dashboard/components/Date";
import CancelIcon from "@mui/icons-material/Cancel";
import useDefaultCurrency from "../../utility/useDefaultCurrency";

const SalesFilter = ({
  isOpen,
  onClose,
  setCurrentPage,
  currentPage,
  setFilteredData,
  filteredData,
  setShowModal,
  fetchOrders,
}) => {
  const [formData, setFormData] = useState({
    start_amount_range: 1000,
    end_amount_range: 500000,
    cashier_name: "",
    customer_name: "",
    discount: "", // renamed from loyalty_plan
    payment_method: "",
    start_date: "",
    end_date: "",
  });
  const { data } = useDefaultCurrency("admin");
  const currency = useMemo(() => data?.currency?.symbol, [data]);

  const [
    {
      data: cashierList = [],
      isLoading: loadingCashiers,
      isError: errorCashiers,
    },
    {
      data: customerList = [],
      isLoading: loadingCustomers,
      isError: errorCustomers,
    },
    {
      data: discountList = [], // renamed from loyaltyPlan
      isLoading: loadingDiscount,
      isError: errorDiscount,
    },
    {
      data: paymentList = [],
      isLoading: loadingPayments,
      isError: errorPayments,
    },
  ] = useQueries({
    queries: [
      {
        queryKey: ["cashierList"],
        queryFn: () => fetchOnlyData("dashboard/cashiers"),
      },
      {
        queryKey: ["customerList"],
        queryFn: () => fetchOnlyData("dashboard/customers"),
      },
      {
        queryKey: ["discountList"],
        queryFn: () => fetchOnlyData("dashboard/loyalty-plans"),
      },
      {
        queryKey: ["paymentList"],
        queryFn: () => fetchOnlyData("dashboard/payment-methods"),
      },
    ],
  });

  if (!isOpen) return null;

  if (
    loadingCashiers ||
    loadingCustomers ||
    loadingDiscount ||
    loadingPayments
  ) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur">
        <div className="bg-white px-5 py-5 rounded-lg shadow font-semibold text-green-600 text-base tracking-wide">
          Loading filters...
        </div>
      </div>
    );
  }

  if (errorCashiers || errorCustomers || errorDiscount || errorPayments) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur">
        <div className="bg-white px-4 py-4 rounded-lg shadow text-red-500 font-medium text-center text-base">
          Failed to load filter data.
        </div>
      </div>
    );
  }

  const handleCashier = (name) => {
    setFormData((prev) => ({ ...prev, cashier_name: name }));
  };

  const handleCustomer = (name) => {
    setFormData((prev) => ({ ...prev, customer_name: name }));
  };

  const handleDiscount = (name) => {
    setFormData((prev) => ({ ...prev, discount: name }));
  };

  const handlePayment = (method) => {
    setFormData((prev) => ({ ...prev, payment_method: method }));
  };

  const handleDateChange = (year, month, day, type) => {
    setFormData((prevData) => ({
      ...prevData,
      [type]: `${year}-${month}-${day}`,
    }));
  };

  const handleSubmit = () => {
    setCurrentPage(1);
    setFilteredData(formData);
    fetchOrders(currentPage, "", formData);
    setShowModal(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 bg-black/60 backdrop-blur">
      <div className="bg-white rounded shadow-lg w-full max-w-md md:max-w-lg p-3 md:p-6 overflow-y-auto max-h-[88vh] border border-neutral-200 relative ring-1 ring-green-100">
        {/* Header with cancel */}
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h2 className="text-xl md:text-2xl font-extrabold text-neutral-800 tracking-tight px-1">
            Filters
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-neutral-200 transition"
            aria-label="Cancel filter"
            tabIndex={0}
          >
            <CancelIcon
              sx={{
                fontSize: { xs: 20, sm: 24, md: 28 },
              }}
              className="text-gray-500 hover:text-green-600 transition-colors"
            />
          </button>
        </div>
        {/* Filter form */}
        <div className="flex flex-col gap-4">
          {/* Amount Range */}
          <div>
            <label className="block text-sm md:text-base font-semibold mb-2 text-gray-800">
              Amount Range:
              <span className="ml-2 font-bold text-green-700 tracking-tight">{currency}{formData.start_amount_range.toLocaleString()}</span>
              {" "} - <span className="font-bold text-green-700 tracking-tight">{currency}{formData.end_amount_range.toLocaleString()}</span>
            </label>
            <div className="px-1 pt-2 pb-0.5">
              <ReactSlider
                className="w-full h-2 bg-green-50 rounded-full"
                thumbClassName="h-4 w-4 bg-green-600 border-2 border-white shadow rounded-full cursor-pointer transition-all duration-150 outline-none focus:ring-1 focus:ring-green-500"
                trackClassName="bg-gradient-to-r from-green-300 via-green-400 to-green-600 h-2 rounded-full"
                min={1000}
                max={10000000}
                step={1000}
                value={[formData.start_amount_range, formData.end_amount_range]}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    start_amount_range: val[0],
                    end_amount_range: val[1],
                  }))
                }
                pearling
                minDistance={5000}
              />
              <div className="flex justify-between text-xs text-gray-400 pt-1 px-1 font-medium select-none">
                <span className="tracking-wider">{currency}1,000</span>
                <span className="tracking-wider">{currency}10,000,000</span>
              </div>
            </div>
          </div>

          {/* Cashier Name */}
          <div>
            <label className="block text-sm md:text-base font-semibold mb-1 text-gray-800">Cashier</label>
            <div className="rounded-lg border border-gray-200 px-2 py-1.5 bg-gray-50 focus-within:ring-1 focus-within:ring-green-400 shadow-sm">
              <SelectLabels
                categoryData={cashierList}
                text="Select Cashier"
                valueKey="id"
                labelKey="firstname"
                onSelectChange={handleCashier}
              />
            </div>
          </div>

          {/* Customer Name */}
          <div>
            <label className="block text-sm md:text-base font-semibold mb-1 text-gray-800">Customer</label>
            <div className="rounded-lg border border-gray-200 px-2 py-1.5 bg-gray-50 focus-within:ring-1 focus-within:ring-green-400 shadow-sm">
              <SelectLabels
                categoryData={customerList}
                text="Select Customer"
                valueKey="id"
                labelKey="name"
                onSelectChange={handleCustomer}
              />
            </div>
          </div>

          {/* Discount */}
          <div>
            <label className="block text-sm md:text-base font-semibold mb-1 text-gray-800">Discount</label>
            <div className="rounded-lg border border-gray-200 px-2 py-1.5 bg-gray-50 focus-within:ring-1 focus-within:ring-green-400 shadow-sm">
              <SelectLabels
                categoryData={discountList}
                text="Select discount"
                valueKey="id"
                labelKey="name"
                onSelectChange={handleDiscount}
              />
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm md:text-base font-semibold mb-1 text-gray-800">Payment Method</label>
            <div className="rounded-lg border border-gray-200 px-2 py-1.5 bg-gray-50 focus-within:ring-1 focus-within:ring-green-400 shadow-sm">
              <SelectLabels
                categoryData={paymentList}
                text="Select Payment method"
                valueKey="id"
                labelKey="method_name"
                onSelectChange={handlePayment}
              />
            </div>
          </div>

          {/* Date Range - Two Column Responsive */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Start Date */}
            <div>
              <label htmlFor="start-date" className="block text-sm md:text-base font-semibold mb-1 text-gray-800">
                Start Date
              </label>
              <div className="rounded-lg  bg-gray-50 py-2 focus-within:ring-2 focus-within:ring-green-400 shadow transition-all duration-150">
                <Date
                  onDateChange={handleDateChange}
                  type="start_date"
                  id="start-date"
                  className="w-full"
                  value={formData.start_date}
                  placeholder="Select start date"
                />
              </div>
            </div>
            {/* End Date */}
            <div>
              <label htmlFor="end-date" className="block text-sm md:text-base font-semibold mb-1 text-gray-800">
                End Date
              </label>
              <div className="rounded-lg  bg-gray-50 py-2 focus-within:ring-2 focus-within:ring-green-400 shadow transition-all duration-150">
                <Date
                  onDateChange={handleDateChange}
                  type="end_date"
                  id="end-date"
                  className="w-full"
                  value={formData.end_date}
                  placeholder="Select end date"
                  minDate={formData.start_date || undefined}
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col md:flex-row justify-center md:justify-end gap-2 mt-6">
            <button
              onClick={onClose}
              className="border border-neutral-200 bg-white px-4 py-1.5 md:px-6 md:py-2 rounded-full font-medium text-gray-700 hover:bg-neutral-100 hover:border-green-300 focus:ring-1 focus:ring-green-400 focus:outline-none transition shadow"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="bg-green-600 text-white px-6 py-1.5 md:px-8 md:py-2 rounded-full font-bold shadow hover:bg-green-700 focus:ring-1 focus:ring-green-500 focus:outline-none transition"
            >
              Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesFilter;
